import { Injectable } from '@angular/core';
import { AES, enc } from 'crypto-js';
import { environment } from 'src/environments/environment';
import { RuntimeConfigService } from './runtime-config.service';

/**
 * Stores to local storage with AES
 */
@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private TOKEN_KEY: string = 'token';
  private REFRESH_TOKEN: string = 'refresh';
  private ML_TOKEN_KEY: string = 'mltoken';
  private ML_REFRESH_TOKEN: string = 'mlrefresh';
  /** @deprecated Workflow-specific tokens are no longer used. The main GAB token is used for all requests. */
  private WORKFLOW_SERVER_TOKEN: string = 'workflow_token';
  /** @deprecated Workflow-specific tokens are no longer used. The main GAB token is used for all requests. */
  private WORKFLOW_SERVER_REFRESH_TOKEN: string = 'workflow_refresh_token';
  private TOKEN_COPY_KEY: string = 'token_copy';
  private REFRESH_TOKEN_COPY_KEY: string = 'refresh_copy';
  private IMPERSONATE_KEY: string = 'impersonate_key';
  private IS_BILLING_ADMIN: string = 'IsBillingAdmin';
  private IS_PENDING_PAYMENT: string = 'IsPendingPayment';
  private IS_SUBSCRIPTION_EXPIRED: string = 'IsSubscriptionExpired';
  private IS_SUBSCRIPTION_FREE_TRIAL: string = 'IsSubscriptionFreeTrial';
  private USER_ROLE: string = 'UserRole';
  private USERNAME: string = 'UserName';
  private PORTALID: string = 'portal';
  private IS_EDIT_IN_PROCESS: string = 'isEditInProcess';
  private EDIT_PROCESS_FORM_KEY: string = 'editProcessFormKey';
  private portalState: string = '';
  private IS_EXTERNAL_LOGIN: string = 'IsExternalLogin';
  private readonly AUTH_PROVIDER: string = 'authProvider';
  private REDIRECT_URI: string = 'redirect_url';
  private APP_LANGUAGE: string = 'app_language';
  private readonly ENTITY_FRIENDLY_ID_KEY: string = 'activeEntityFriendlyId';

  private readonly BOOTSTRAP_TOKEN_KEY = '_bt';
  private readonly SUPER_ADMIN_RETURN_URL = 'super_admin_return_url';

  constructor(private readonly runtimeConfig: RuntimeConfigService) {}

  private get SECRET(): string | null {
    return this.runtimeConfig.getSecretKey();
  }

  /**
   * Encrypt data. Returns null when secretKey is not yet loaded.
   */
  public encrypt = (rawData: any): string | null => {
    const secret = this.SECRET;
    if (!secret) return null;
    return AES.encrypt(rawData, secret).toString();
  };

  /**
   * Decrypt data. Returns null when secretKey is not yet loaded or decryption fails.
   */
  public decrypt = (cipherData: any): any | null => {
    try {
      const secret = this.SECRET;
      if (!secret) return null;
      if (cipherData !== null && cipherData !== undefined)
        return AES.decrypt(cipherData, secret).toString(enc.Utf8);
    } catch (e) {
      return null;
    }
    return null;
  };

  /**
   * Save a raw (unencrypted) bootstrap token so the config API can be called
   * before secretKey is available.
   */
  saveBootstrapToken = (token: string) => {
    localStorage.setItem(this.BOOTSTRAP_TOKEN_KEY, token);
  };

  /**
   * Read the raw bootstrap token (used by APP_INITIALIZER and cacheAuthData fallback).
   */
  getBootstrapToken = (): string | null => {
    return localStorage.getItem(this.BOOTSTRAP_TOKEN_KEY);
  };

  /**
   * Remove the bootstrap token (called on logout).
   */
  clearBootstrapToken = () => {
    localStorage.removeItem(this.BOOTSTRAP_TOKEN_KEY);
  };

  /**
   * Set item to storage with encryption
   * @param key of data
   * @param data to store
   */
  public setStorageItem = (key: string, data: any) => {
    const encrypted = this.encrypt(data);
    if (encrypted == null) return;
    localStorage.setItem(key, encrypted);
  };

  /**
   * Get item to storage with decryption
   * @param key of data
   */
  public getStorageItem = (key: string): string | null =>
    this.decrypt(localStorage.getItem(key));

  /**
   * Save token
   * @param token
   */
  saveToken = (token: string, refresh: string) => {
    this.setStorageItem(this.TOKEN_KEY, token);
    this.setStorageItem(this.REFRESH_TOKEN, refresh);
  };

  /**
   * Save ML token
   */
  saveMLToken = (token: string, refresh: string) => {
    this.setStorageItem(this.ML_TOKEN_KEY, token);
    this.setStorageItem(this.ML_REFRESH_TOKEN, refresh);
  };

  /**
   * @deprecated No-op. Workflow-specific tokens are no longer used.
   * The main GAB token (getToken()) is used for all workflow requests.
   */
  saveWorkflowToken = (_token: string, _refresh: string) => {
  }

  /**
   *  Save username
   */
  saveUsername = (username: string) => {
    this.setStorageItem(this.USERNAME, username);
  };

  /**
   *  Save Portal Id
   */
  savePortalId = (id: string | null) => {
    if (id === null || id === undefined || id === '') {
      localStorage.removeItem(this.PORTALID);
    } else {
      this.setStorageItem(this.PORTALID, id);
    }
  };
  /**
   *  Save Portal Id
   */
  saveRedirectURL = (id: string) => {
    this.setStorageItem(this.REDIRECT_URI, id);
  };

  /**
   *  Save Logo
   */
  saveLogo = (logoData: string) => {
    this.setStorageItem('logo-data', logoData);
  };

  /**
   *  Save Icon
   */
  saveIconData = (iconData: Object) => {
    this.setStorageItem('icon-data', JSON.stringify(iconData));
  };
  
  saveEditInProcess = (reportId:string,formKey: string) => {
    this.setStorageItem(reportId+this.IS_EDIT_IN_PROCESS, 'true');
    this.setStorageItem(reportId+this.EDIT_PROCESS_FORM_KEY, formKey);
  };
  
  removeEditProcess = (reportId:string) => {
    if(localStorage.getItem(reportId+this.IS_EDIT_IN_PROCESS) && localStorage.getItem(reportId+this.EDIT_PROCESS_FORM_KEY)){
      localStorage.removeItem(reportId+this.IS_EDIT_IN_PROCESS);
      localStorage.removeItem(reportId+this.EDIT_PROCESS_FORM_KEY);
    }
  };

  getIsEditInProcess(reportId:string){
    return this.getStorageItem(reportId+this.IS_EDIT_IN_PROCESS)
  }
  getEditProcessKey(reportId:string){
    return this.getStorageItem(reportId+this.EDIT_PROCESS_FORM_KEY) || '';
  }
  /**
   * Save stripe subscription flags
   * @param token
   */
  saveSubscriptionFlags = (
    isBillingAdmin: string,
    isPendingPayment: string,
    isSubscriptionExpired: string,
    isSubscriptionFreeTrial: string,
    role: string
  ) => {
    this.setStorageItem(this.IS_BILLING_ADMIN, isBillingAdmin);
    this.setStorageItem(this.IS_PENDING_PAYMENT, isPendingPayment);
    this.setStorageItem(this.IS_SUBSCRIPTION_EXPIRED, isSubscriptionExpired);
    this.setStorageItem(
      this.IS_SUBSCRIPTION_FREE_TRIAL,
      isSubscriptionFreeTrial
    );
    this.setStorageItem(this.USER_ROLE, role);
  };

  saveUserRole = (role: string) => {
    this.setStorageItem(this.USER_ROLE, role);
  };

  /**
   * Save Impersonation Id
   */
  saveImpersonateId = (id: string) => {
    this.setStorageItem(this.IMPERSONATE_KEY, id);
  };
  /**
   * Save App Language
   */
  saveLanguage = (language: string) => {
    this.setStorageItem(this.APP_LANGUAGE, language);
  };

  /**
   * Save if User is external login
   * @param flag for externalLogin
   */
  setIsExternalLogin = (flag: string) => {
    localStorage.setItem(this.IS_EXTERNAL_LOGIN, flag);
  };
  /**
   * Save if User is external login
   * @param flag for externalLogin
   */
  removeIsExternalLogin = () => {
    localStorage.removeItem(this.IS_EXTERNAL_LOGIN);
  };

  setAuthProvider = (provider: 'auth0' | 'og-auth0') => {
    localStorage.setItem(this.AUTH_PROVIDER, provider);
  };

  getAuthProvider = (): string | null =>
    localStorage.getItem(this.AUTH_PROVIDER);

  removeAuthProvider = () => {
    localStorage.removeItem(this.AUTH_PROVIDER);
  };

  /**
   * Clear stored token
   */
  clearToken = () => {
    localStorage.removeItem(this.TOKEN_KEY);
  };

  clearLogo = () => {
    localStorage.removeItem('logo-data');
  }
  clearIconData = () => {
    localStorage.removeItem('icon-data');
  }

  /**
   * Clear Impersonate Id
   */
  clearImpersonateId = () => {
    localStorage.removeItem(this.IMPERSONATE_KEY);
  };

  saveSuperAdminReturnUrl = (url: string) => {
    localStorage.setItem(this.SUPER_ADMIN_RETURN_URL, url);
  };

  getSuperAdminReturnUrl = (): string | null => {
    return localStorage.getItem(this.SUPER_ADMIN_RETURN_URL);
  };

  clearSuperAdminReturnUrl = () => {
    localStorage.removeItem(this.SUPER_ADMIN_RETURN_URL);
  };

  /**
   * Clear Impersonate Id
   */
  clearRedirectURL = () => {
    localStorage.removeItem(this.REDIRECT_URI);
  };

  /**
   * Clear Copy Tokens
   */
  clearCopyTokens = () => {
    localStorage.removeItem(this.TOKEN_COPY_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_COPY_KEY);
    localStorage.removeItem(this.IMPERSONATE_KEY);
  };

  /**
   * Copy Tokens
   */
  copyTokens = () => {
    if (
      !this.getStorageItem(this.TOKEN_COPY_KEY) &&
      !this.getStorageItem(this.REFRESH_TOKEN_COPY_KEY)
    ) {
      this.setStorageItem(
        this.TOKEN_COPY_KEY,
        this.getStorageItem(this.TOKEN_KEY) || ''
      );
      this.setStorageItem(
        this.REFRESH_TOKEN_COPY_KEY,
        this.getStorageItem(this.REFRESH_TOKEN) || ''
      );
    }
  };

  /**
   * Restore Tokens
   */
  restoreTokens = () => {
    this.setStorageItem(
      this.TOKEN_KEY,
      this.getStorageItem(this.TOKEN_COPY_KEY) || ''
    );
    this.setStorageItem(
      this.REFRESH_TOKEN,
      this.getStorageItem(this.REFRESH_TOKEN_COPY_KEY) || ''
    );
    this.clearCopyTokens();
  };

  /**
   * Clear all
   */
  clear = (isClearStorage: boolean, isPortal: string | null = null) => {
    let itemsKey: Array<string> = [
      'selectedEmployee',
      'unSavedResource',
      'unSavedActivity',
      'token_copy',
      'refresh_copy',
      'redirect_url',
      'app_language',
    ];
    if (isPortal != null){
      this.portalState = isPortal;
    }else{
      this.portalState = '';
    }
    if (isClearStorage && this.portalState === '') {
      localStorage.clear();
      sessionStorage.removeItem('wf_iframe_auth_done');
      this.clearBootstrapToken();
    } else {
      // Iterate using Storage API to reliably access keys
      for (let i = window.localStorage.length - 1; i >= 0; i--) {
        const key = window.localStorage.key(i);
        if (!key) continue;
        if (!itemsKey.includes(key)) window.localStorage.removeItem(key);
      }
    }
    if(this.portalState != ''){
      this.setStorageItem(this.PORTALID, this.portalState);
    }
  };

  /**
   * Get token. Falls back to bootstrap token when secretKey is not loaded yet.
   */
  getToken = (): string | null => {
    const encrypted = this.getStorageItem(this.TOKEN_KEY);
    if (encrypted) return encrypted;
    return this.getBootstrapToken();
  };

  getRefresh = (): string | null => this.getStorageItem(this.REFRESH_TOKEN);

  getMLToken = (): string | null => this.getStorageItem(this.ML_TOKEN_KEY);

  getMLRefresh = (): string | null => this.getStorageItem(this.ML_REFRESH_TOKEN);

  /** @deprecated Returns the main GAB token. Workflow-specific tokens are no longer used. */
  getWorkflowToken = (): string | null => this.getStorageItem(this.TOKEN_KEY);

  /** @deprecated Returns the main GAB refresh token. Workflow-specific tokens are no longer used. */
  getWorkflowRefreshToken = (): string | null => this.getStorageItem(this.REFRESH_TOKEN);

  getCopyToken = (): string | null => this.getStorageItem(this.TOKEN_COPY_KEY);

  getIsBillindAdmin = (): string | null =>
    this.getStorageItem(this.IS_BILLING_ADMIN);

  getIsPendingPayment = (): string | null =>
    this.getStorageItem(this.IS_PENDING_PAYMENT);

  getIsSubscriptionExpired = (): string | null =>
    this.getStorageItem(this.IS_SUBSCRIPTION_EXPIRED);

  getIsSubscriptionFreeTrial = (): string | null =>
    this.getStorageItem(this.IS_SUBSCRIPTION_FREE_TRIAL);

  getUserRole = (): string | null => this.getStorageItem(this.USER_ROLE);

  getUserName = (): string | null => this.getStorageItem(this.USERNAME);

  getImpersonateId = (): string | null =>
    this.getStorageItem(this.IMPERSONATE_KEY);

  getCopyRefresh = (): string | null =>
    this.getStorageItem(this.REFRESH_TOKEN_COPY_KEY);

  getPortalId = (): string | null =>
    this.getStorageItem(this.PORTALID);

  getRedirectURL = (): string | null =>
    this.getStorageItem(this.REDIRECT_URI);

  getLogo = (): string | null =>
    this.getStorageItem('logo-data');

  getAppLanguage = (): string | null =>
    this.getStorageItem(this.APP_LANGUAGE);
    
  getIconData = (): any | null =>
    JSON.parse(JSON.stringify(this.getStorageItem('icon-data')) || '');

  getIsExternalLogin = (): boolean | null =>
    localStorage.getItem(this.IS_EXTERNAL_LOGIN) === 'true';

  /**
   * Save Entity Friendly Id
   */
  setEntityFriendlyId = (entityFriendlyId: string | null) => {
    if (entityFriendlyId) {
      this.setStorageItem(this.ENTITY_FRIENDLY_ID_KEY, entityFriendlyId);
    } else {
      localStorage.removeItem(this.ENTITY_FRIENDLY_ID_KEY);
    }
  };

  /**
   * Get Entity Friendly Id
   */
  getEntityFriendlyId = (): string | null =>
    this.getStorageItem(this.ENTITY_FRIENDLY_ID_KEY);
}
